import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { TextField, Button, MenuItem, Container, Typography, Grid, Paper, FormControl, Box, Avatar } from '@mui/material';
import { CircularProgress } from '@mui/material';
import HttpsIcon from '@mui/icons-material/Https';
import Swal from 'sweetalert2';
import { message } from 'antd';
import 'sweetalert2/dist/sweetalert2.min.css';

const validationSchema = Yup.object({
  productName: Yup.string().required('Nombre del artículo es requerido'),
  productQuantity: Yup.number()
    .required('La cantidad de la prenda es requerida')
    .min(1, 'La cantidad debe ser al menos 1')
    .integer('Debe ser un número entero'),
  productCode: Yup.string().required('El código de la prenda es requerido'),
  serviceID: Yup.string().required('El tipo de prenda es requerido'),

  productStatus: Yup.string().required('El estado de la prenda es requerido'),
});

const formatValues = (values) => ({
  name: values.productName,
  description: values.productDescription,
  quantity: values.productQuantity,
  category: values.productCategory,
  serviceType: values.serviceType,
  code: values.productCode,
  status: values.productStatus,
  size: values.productSize,
  stock: values.productStock,
  serviceID: values.serviceID,
  createdby: 1
});

function AddProduct() {
  const [services, setServices] = useState([]);
  const [permissionError, setPermissionError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();

  const checkPermissions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      messageApi.error('No se encontró el token de autenticación.');
      setPermissionError(true);
      window.location.href = '/sign-in'; // Redirect to login if no token
      return;
    }
    setPermissionError(false);
    setIsLoading(true);
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/users/check-modify-role-permissions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 403) {
        messageApi.error('No tienes permisos para acceder a esta sección.');
        setPermissionError(true);
        throw new Error('No tienes permisos para acceder a esta sección.');
      }
    } catch (error) {
      messageApi.error(error.message || 'Error al verificar permisos.');
      throw error; // Re-throw to handle it in the calling function
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  const getServices = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/services/clothes', {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!data.error) {
        setServices(data);
      }
    } catch (error) {
      console.error('Error:', error);
      messageApi.error('Error al obtener servicios.');
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    const token = localStorage.getItem('token');
    const { isConfirmed } = await Swal.fire({
      title: 'Confirmar creación de artículo',
      text: '¿Estás seguro de registrar este artículo?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4caf50',
      cancelButtonColor: '#f44336',
      confirmButtonText: 'Registrar',
      cancelButtonText: 'Cancelar',
      allowOutsideClick: false,
      allowEscapeKey: false
    });

    if (!isConfirmed) return;

    Swal.fire({
      title: 'Creando artículo...',
      text: 'Por favor espera mientras se crea el artículo.',
      icon: 'info',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const article = formatValues(values);
      const response = await fetch('https://dprincess-training.servehttp.com/create-inventory-item', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(article)
      });

      if (!response.ok) {
        Swal.close();
        if (response.status === 409) {
          throw new Error('El código del artículo ya está registrado');
        }
        throw new Error(response.statusText);
      }

      const data = await response.json();
      Swal.close();
      await Swal.fire({
        title: 'Artículo registrado',
        text: data.message,
        icon: 'success',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#4caf50'
      });
      resetForm();
    } catch (error) {
      Swal.close();
      await Swal.fire({
        title: 'Error',
        text: error.message || 'Ha ocurrido un error inesperado',
        icon: 'error',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#f44336'
      });
    }
  };

  useEffect(() => {
    getServices();
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        {contextHolder}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{
        p: 4,
        borderRadius: '16px',
        background: 'linear-gradient(145deg, #f8f9fa 0%, #ffffff 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.05)'
      }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{
          color: '#531dab',
          fontWeight: 700,
          mb: 4,
          textTransform: 'uppercase',
          letterSpacing: '1.5px',
          position: 'relative',
          '&:after': {
            content: '""',
            display: 'block',
            width: '60px',
            height: '3px',
            backgroundColor: '#531dab',
            margin: '16px auto 0'
          }
        }}>
          Registrar Prenda
        </Typography>

        <Formik
          initialValues={{
            productName: '',
            productDescription: '',
            productQuantity: '',
            productCategory: '',
            productCode: '',
            productStatus: 'activo',
            serviceName: '',
            serviceType: '',
            productSize: '',
            productStock: 'disponible',
            serviceID: '',
            createdby: 1
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            isSubmitting,
            errors,
            touched,
            setFieldValue,
            handleSubmit,
            handleChange,
            values,
            resetForm
          }) => (
            <Form>
              <Grid container spacing={3}>
                {/* Campos del formulario */}
                {['productName', 'productDescription', 'productQuantity', 'productCode'].map((field) => (
                  <Grid item xs={12} md={6} key={field}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      id={field}
                      name={field}
                      label={
                        field === 'productName' ? 'Nombre de la prenda' :
                          field === 'productDescription' ? 'Descripción' :
                            field === 'productQuantity' ? 'Cantidad' : 'Código'
                      }
                      type={field === 'productQuantity' ? 'number' : 'text'}
                      value={values[field]}
                      onChange={handleChange}
                      error={touched[field] && Boolean(errors[field])}
                      helperText={touched[field] && errors[field]}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          '&:hover fieldset': { borderColor: '#531dab' },
                          '&.Mui-focused fieldset': { borderColor: '#531dab' }
                        }
                      }}
                    />
                  </Grid>
                ))}

                <Grid item xs={12} md={6}>
                  <Field name="serviceID">
                    {({ field, form }) => (
                      <FormControl fullWidth>
                        <TextField
                          select
                          label="Tipo de ropa"
                          id="serviceID"
                          name="serviceID"
                          value={values.serviceID || ''}
                          onChange={(event) => {
                            const selectedServiceID = event.target.value;
                            form.setFieldValue(field.name, selectedServiceID);

                            // Encontrar el servicio seleccionado para obtener el nombre
                            const selectedService = services.find(service => service.serviceID.toString() === selectedServiceID);
                            form.setFieldValue('serviceName', selectedService.serviceName);
                            form.setFieldValue('serviceType', selectedService.serviceType);
                            if (Number(selectedService.serviceID) === 12) {
                              form.setFieldValue('productCategory', 'xv');
                            } else if (Number(selectedService.serviceID) === 22) {
                              form.setFieldValue('productCategory', 'wedding');
                            } else if (Number(selectedService.serviceID) === 35) {
                              form.setFieldValue('productCategory', 'men');
                            } else if (Number(selectedService.serviceID) === 36) {
                              form.setFieldValue('productCategory', 'child');
                            } else if (Number(selectedService.serviceID) === 13) {
                              form.setFieldValue('productCategory', 'personal');
                            } else if (Number(selectedService.serviceID) === 14) {
                              form.setFieldValue('productCategory', 'girls');
                            } 
                          }}
                          error={touched.serviceID && Boolean(errors.serviceID)}
                          helperText={touched.serviceID && errors.serviceID}
                          variant="outlined"
                          sx={{
                            width: '100%',
                            '& .MuiSelect-select': { py: 2 },
                            '& .MuiOutlinedInput-root': { borderRadius: '8px' }
                          }}
                        >
                          <MenuItem value="">Selecciona un servicio</MenuItem>
                          {services.map((service) => (
                            <MenuItem key={service.serviceID} value={service.serviceID.toString()}>
                              {service.serviceName}
                            </MenuItem>
                          ))}
                        </TextField>
                      </FormControl>
                    )}
                  </Field>
                </Grid>

                {
                  values.serviceType && values.serviceType.toLocaleLowerCase() === 'vestidos' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Talla"
                        id="productSize"
                        name="productSize"
                        value={values.productSize}
                        onChange={handleChange}
                        error={touched.productSize && Boolean(errors.productSize)}
                        helperText={touched.productSize && errors.productSize}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                          '& .MuiSelect-select': { py: 2 }
                        }}
                      >
                        <MenuItem value="short">Vestido corto</MenuItem>
                        <MenuItem value="long">Vestido largo</MenuItem>
                      </TextField>
                    </Grid>
                  )
                }

                {
                  values.serviceType && values.serviceType.toLocaleLowerCase() === 'trajes' && Number(values.serviceID) === 35 && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Talla"
                        id="productSize"
                        name="productSize"
                        value={values.productSize}
                        onChange={handleChange}
                        error={touched.productSize && Boolean(errors.productSize)}
                        helperText={touched.productSize && errors.productSize}
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                          '& .MuiSelect-select': { py: 2 }
                        }}
                      >
                        <MenuItem value="short">Traje corto</MenuItem>
                        <MenuItem value="long">Traje largo</MenuItem>
                      </TextField>
                    </Grid>
                  )
                }

                {
                  values.serviceType && values.serviceType.toLocaleLowerCase() === 'trajes' && Number(values.serviceID) === 36 && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        name="productSize"
                        value={values.productSize}
                        onChange={handleChange}
                        placeholder="Ingresa la talla"
                      />
                    </Grid>
                  )
                }

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Estado del artículo"
                    id="productStatus"
                    name="productStatus"
                    value={values.productStatus}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{
                      '& .MuiSelect-select': { py: 2 },
                      '& .MuiOutlinedInput-root': { borderRadius: '8px' }
                    }}
                  >
                    {['activo', 'inactivo'].map((status) => (
                      <MenuItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sx={{ mt: 4 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    disabled={isSubmitting}
                    sx={{
                      py: 2,
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderRadius: '12px',
                      backgroundColor: '#531dab',
                      '&:hover': {
                        backgroundColor: '#3a1377',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(83, 29, 171, 0.3)'
                      },
                      '&:disabled': {
                        backgroundColor: '#e0e0e0',
                        color: '#a0a0a0',
                        transform: 'none'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    {isSubmitting ? (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span className="spinner"></span>
                        Registrando...
                      </span>
                    ) : (
                      'Registrar Artículo'
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
}

export default AddProduct;